/**
 * ModConnection — one mod-source-to-mod-target wiring in the synth.
 *
 * This Phase 4 shape is minimal: a source module, a target (module id +
 * parameter name), and a bipolar depth. Phase 10 added `curve`, which
 * shapes the macro source's 0..1 value before it scales by depth —
 * lets one macro position drive Cutoff exponentially while Reverb
 * Mix follows linearly. Curves only take effect on macro sources;
 * audio-path mods (LFO, S&H, per-note) ignore the field since their
 * routing happens entirely on AudioParams.
 */

export type ModCurve = 'linear' | 'exp' | 'log' | 's';

/** Available curves in display order — used by the Mod Matrix UI to
 *  populate the curve picker. */
export const MOD_CURVES: readonly ModCurve[] = ['linear', 'exp', 'log', 's'];

export interface ModConnection {
  readonly id: string;
  /** The source module id. For §5 per-note sources (velocity, etc.) this
   *  will be a sentinel in the future; Phase 4 only supports preset-scope
   *  module sources. */
  readonly sourceModuleId: string;
  /** Target module id — addresses the *template* module, not a specific voice. */
  readonly targetModuleId: string;
  /** Target parameter name as declared on the module's NumericParameter. */
  readonly targetParam: string;
  /** -1..+1. Negative depth inverts polarity. */
  readonly depth: number;
  /**
   * Per-connection range multiplier applied on top of the target's own
   * modScale — lets the user dial in sub-slider-precision modulation
   * (e.g. fine flanger depth on delay time) without sacrificing the
   * slider's resolution. Effective mod = depth × rangeMul × modScale.
   * Optional for back-compat; missing means "full range" (1.0).
   */
  readonly rangeMul?: number;
  /**
   * Per-connection curve applied to the source value BEFORE depth /
   * rangeMul scaling. Only meaningful on macro sources, where the
   * router writes target Parameter values directly; audio-path
   * sources ignore this field. Optional for back-compat; missing
   * means 'linear'.
   */
  readonly curve?: ModCurve;
}

/**
 * Apply `curve` to a value `v` (0..1 macro range). Returns 0..1.
 *
 *   linear → x                (no shaping)
 *   exp    → x²               (slow start, fast end — feels like an envelope)
 *   log    → √x               (fast start, slow end — useful for cutoff sweeps)
 *   s      → smoothstep        (sigmoid-ish, eases both ends)
 *
 * Negative inputs are mirrored — important for connections that
 * happen to feed a bipolar parameter via depth, even though the
 * macro itself is unipolar.
 */
export function shapeValue(v: number, curve: ModCurve | undefined): number {
  const c = curve ?? 'linear';
  if (c === 'linear') return v;
  const sign = v < 0 ? -1 : 1;
  const abs = Math.abs(v);
  switch (c) {
    case 'exp':
      return sign * abs * abs;
    case 'log':
      return sign * Math.sqrt(abs);
    case 's':
      // smoothstep(t) = 3t² - 2t³  → eases at both 0 and 1.
      return sign * (abs * abs * (3 - 2 * abs));
  }
}

/** Normalize an incoming rangeMul into a positive finite value. */
export function clampRangeMul(mul: number | undefined): number {
  if (mul === undefined) return 1;
  if (!Number.isFinite(mul)) return 1;
  return Math.max(0, Math.min(1, mul));
}

/** Sanitize an incoming curve string. Unknown values fall back to
 *  'linear' so old presets that never knew about curves stay sane. */
export function clampCurve(curve: ModCurve | string | undefined): ModCurve {
  if (curve === undefined) return 'linear';
  return MOD_CURVES.includes(curve as ModCurve)
    ? (curve as ModCurve)
    : 'linear';
}

/** Serialized form of a connection. Same shape as the runtime type in Phase 4,
 *  but this alias documents intent and gives future migrations a landing pad. */
export type ModConnectionState = ModConnection;

export function mintConnectionId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `conn-${crypto.randomUUID().slice(0, 8)}`;
  }
  return `conn-${Math.random().toString(36).slice(2, 10)}`;
}

/** Normalize an incoming depth into the [-1, 1] range. */
export function clampDepth(depth: number): number {
  if (Number.isNaN(depth)) return 0;
  return Math.max(-1, Math.min(1, depth));
}
